import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { stashAgentPrefill } from "../../utils/agentPrefill";
import {
  clearKnowledgePrefill,
  clearOpenAddPanel,
  markKeepKnowledgePrefill,
  stashKnowledgePrefill,
} from "../../utils/knowledgePrefill";
import { apiFetch } from "../../services/api";
import { useV2EnrichmentReview } from "../state/V2EnrichmentReviewContext";
import { V2_ROUTES } from "../state/useV2Navigation";
import { GUIDE_EVENT } from "./guideEvents";
import {
  alignChapterToWizardStep,
  applyChapterCompletion,
  applyGuideEvent,
  chapterIndexOf,
  expectedLocationForChapter,
  isGuideFocusChapter,
  isGuideNavAllowed,
  isGuideOffPath,
} from "./guideProgress";
import {
  clearGuideState,
  readGuideInviteDismissed,
  readGuideOptedOut,
  readGuideState,
  requestInboxOpenJob,
  writeGuideInviteDismissed,
  writeGuideOptedOut,
  writeGuideState,
} from "./guideStorage";
import {
  ensureGuideWizardPrefill,
  guideNeedsAddKnowledgeRoute,
  guideNeedsInboxRoute,
} from "./guideWizardResume";
import {
  readOnboardingVideoDismissed,
  writeOnboardingVideoDismissed,
} from "./onboardingVideo";
import { ADD_KNOWLEDGE_FIRST_APP, getScript } from "./scripts/addKnowledgeFirstApp";

const GuideContext = createContext(null);

export function ProductGuideProvider({
  children,
  onNavigate,
  onOpenHomeAdd,
  onOpenHomeAsk,
  currentRoute = "",
}) {
  const { enrichmentJobs, refreshEnrichmentJobs } = useV2EnrichmentReview();

  const [state, setState] = useState(() => {
    const s = readGuideState();
    // Before Home mounts: drop leftover open-add from the wizard so refresh
    // during Building does not bounce through Add knowledge.
    if (s && guideNeedsInboxRoute(s)) {
      clearOpenAddPanel();
      clearKnowledgePrefill();
    }
    return s;
  });
  const [inviteDismissed, setInviteDismissed] = useState(() =>
    readGuideInviteDismissed()
  );
  const [onboardingVideoDismissed, setOnboardingVideoDismissed] = useState(() =>
    readOnboardingVideoDismissed()
  );
  const [guideOptedOut, setGuideOptedOut] = useState(() => readGuideOptedOut());
  /**
   * null = count not loaded yet (hide invite to avoid flashing for tenants with data).
   * true = empty repository — newcomer invite may show.
   */
  const [mapIsEmpty, setMapIsEmpty] = useState(null);
  /** Home hub | ask | add — updated via guide events from V2HomePage. */
  const [homePanel, setHomePanel] = useState("hub");
  /** Map Add knowledge panel open (mobile primary path). */
  const [mapAddOpen, setMapAddOpen] = useState(false);
  /** Short-lived Soft-focus cue after a blocked leave / auto-snap. */
  const [focusNudge, setFocusNudge] = useState(false);
  /** Live Add-knowledge wizard UI (step + whether entity/notes exist). */
  const [wizardUi, setWizardUi] = useState(() => {
    const s = readGuideState();
    if (!s?.scriptId) return null;
    // Building / review: do not resurrect stale source-step wizard UI.
    if (guideNeedsInboxRoute(s)) return null;
    const step =
      String(s.wizardStep || "").trim() ||
      expectedLocationForChapter(s.chapterId)?.wizard ||
      null;
    if (!step && !s.entityId) return null;
    return {
      step: step || "anchor",
      hasAnchor: Boolean(String(s.entityId || "").trim()),
      hasNotes: Boolean(s.sampleNotesLoaded),
    };
  });

  const persist = useCallback((next) => {
    setState(next);
    if (next?.scriptId) writeGuideState(next);
    else clearGuideState();
  }, []);

  const dismissInvite = useCallback(() => {
    setInviteDismissed(true);
    writeGuideInviteDismissed(true);
  }, []);

  const dismissOnboardingVideo = useCallback(() => {
    setOnboardingVideoDismissed(true);
    writeOnboardingVideoDismissed(true);
  }, []);

  const resetInviteForEmptyMap = useCallback(() => {
    setInviteDismissed(false);
    writeGuideInviteDismissed(false);
  }, []);

  const resetOnboardingVideoForEmptyMap = useCallback(() => {
    setOnboardingVideoDismissed(false);
    writeOnboardingVideoDismissed(false);
  }, []);

  const resetGuideOptOutForEmptyMap = useCallback(() => {
    setGuideOptedOut(false);
    writeGuideOptedOut(false);
  }, []);

  // Empty-map gate: show newcomer invite only when the repository has no entities.
  // After a catalog wipe, bring the invite back; once data exists, keep it hidden.
  const mapCountRef = useRef(null);
  const checkInFlightRef = useRef(false);
  useEffect(() => {
    let cancelled = false;

    const checkMap = async () => {
      if (checkInFlightRef.current) return;
      checkInFlightRef.current = true;
      try {
        // approved_only=true hits the catalog cache; empty-map detection does not
        // need proposed counts (and avoids stacking slow contributor browse polls).
        const res = await apiFetch(
          "/api/entity-types/counts?approved_only=true",
          { cache: "no-store", timeout: 60000 }
        );
        if (!res?.ok || cancelled) return;
        const data = await res.json();
        const total = Number(data?.total_entities || 0);
        const prev = mapCountRef.current;
        mapCountRef.current = total;
        const empty = total <= 0;
        setMapIsEmpty(empty);

        if (!empty) return;

        resetInviteForEmptyMap();
        // Re-offer first-run after a catalog wipe (had entities → empty),
        // OR when the app loads already-empty (DB wiped while closed) —
        // otherwise dp_guide_opted_out sticks and the journey rail never returns.
        if (prev != null && prev > 0) {
          resetOnboardingVideoForEmptyMap();
          resetGuideOptOutForEmptyMap();
          persist(null);
        } else if (prev === null) {
          resetGuideOptOutForEmptyMap();
        }
      } catch {
        /* ignore — leave mapIsEmpty unchanged so we don't flash the invite */
      } finally {
        checkInFlightRef.current = false;
      }
    };

    void checkMap();
    const intervalId = window.setInterval(checkMap, 20000);
    let focusTimer = null;
    const onFocus = () => {
      if (focusTimer) window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        void checkMap();
      }, 750);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      if (focusTimer) window.clearTimeout(focusTimer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [
    persist,
    resetGuideOptOutForEmptyMap,
    resetInviteForEmptyMap,
    resetOnboardingVideoForEmptyMap,
  ]);

  // After refresh / wrong route: put the user back on Add knowledge mid-guide.
  useEffect(() => {
    if (!state?.scriptId) return;
    if (!guideNeedsAddKnowledgeRoute(state)) return;
    const route = String(currentRoute || "").trim();
    if (route === V2_ROUTES.addKnowledge || route === "add-knowledge") return;
    ensureGuideWizardPrefill(state);
    if (typeof onOpenHomeAdd === "function") onOpenHomeAdd();
    else onNavigate?.(V2_ROUTES.addKnowledge);
  }, [
    currentRoute,
    onNavigate,
    onOpenHomeAdd,
    state,
    state?.chapterId,
    state?.scriptId,
    state?.jobId,
  ]);

  // After refresh during Building / Review: land on Inbox (not Add knowledge).
  // Leftover dp_open_add_panel from the wizard must not win on Home mount.
  useEffect(() => {
    if (!state?.scriptId) return;
    if (!guideNeedsInboxRoute(state)) return;
    clearOpenAddPanel();
    clearKnowledgePrefill();
    const route = String(currentRoute || "").trim();
    if (route === V2_ROUTES.insightsInbox || route === "insights-inbox") {
      const jobId = String(state.jobId || "").trim();
      if (jobId) requestInboxOpenJob(jobId);
      return;
    }
    const jobId = String(state.jobId || "").trim();
    if (jobId) requestInboxOpenJob(jobId);
    onNavigate?.(V2_ROUTES.insightsInbox);
  }, [
    currentRoute,
    onNavigate,
    state,
    state?.chapterId,
    state?.jobId,
    state?.scriptId,
  ]);

  const script = useMemo(
    () => (state?.scriptId ? getScript(state.scriptId) : null),
    [state?.scriptId]
  );

  const chapterIndex = script
    ? chapterIndexOf(script, state?.chapterId)
    : 0;
  const chapter = script?.chapters?.[chapterIndex] || null;

  const patchState = useCallback((partial) => {
    setState((prev) => {
      if (!prev?.scriptId) return prev;
      const next = { ...prev, ...partial };
      writeGuideState(next);
      return next;
    });
  }, []);

  /** Replace tour state with event catch-up (marks prior chapters done). */
  const applyEventProgress = useCallback(
    (eventType, extra = {}) => {
      setState((prev) => {
        if (!prev?.scriptId) return prev;
        const s = getScript(prev.scriptId);
        if (!s) return prev;
        let next = applyGuideEvent(s, prev, eventType);
        if (next === prev) {
          // Unknown event — still merge extras (e.g. jobId).
          next = { ...prev, ...extra };
        } else {
          next = { ...next, ...extra };
        }
        writeGuideState(next);
        return next;
      });
    },
    []
  );

  const goToChapter = useCallback(
    (chapterId) => {
      if (!script) return;
      const idx = chapterIndexOf(script, chapterId);
      const id = script.chapters[idx]?.id;
      if (!id) return;
      patchState({ chapterId: id });
    },
    [patchState, script]
  );

  const startAddKnowledgeGuide = useCallback(() => {
    setGuideOptedOut(false);
    writeGuideOptedOut(false);
    const next = {
      scriptId: ADD_KNOWLEDGE_FIRST_APP.id,
      chapterId: "intent",
      entityId: "",
      entityName: "",
      jobId: "",
      milestonesCompleted: [],
      startedAt: Date.now(),
    };
    persist(next);
  }, [persist]);

  const skipGuide = useCallback(() => {
    setGuideOptedOut(true);
    writeGuideOptedOut(true);
    persist(null);
  }, [persist]);

  const beginOnAddKnowledge = useCallback(() => {
    const s = script || ADD_KNOWLEDGE_FIRST_APP;
    stashKnowledgePrefill({
      scopeType: "entity",
      intent: "create",
      suggestedName: s.sampleName,
      suggestedType: "application",
      startStep: "anchor",
      pasteText: s.sampleNotes,
    });
    markKeepKnowledgePrefill();
    setState((prev) => {
      const base = prev?.scriptId
        ? prev
        : {
            scriptId: ADD_KNOWLEDGE_FIRST_APP.id,
            entityId: "",
            entityName: "",
            jobId: "",
            milestonesCompleted: [],
            startedAt: Date.now(),
          };
      // Notes travel with prefill (applied on the source step after create).
      // Do not mark sampleNotesLoaded yet — strip tips follow live wizard UI.
      const next = applyChapterCompletion(
        s,
        {
          ...base,
          entityName: String(base.entityName || s.sampleName || "").trim(),
        },
        "intent"
      );
      writeGuideState(next);
      return next;
    });
    setHomePanel("add");
    try {
      window.dispatchEvent(
        new CustomEvent("dp-open-home-panel", { detail: { panel: "add" } })
      );
    } catch {
      /* ignore */
    }
    if (typeof onOpenHomeAdd === "function") onOpenHomeAdd();
    else onNavigate?.(V2_ROUTES.home);
  }, [onNavigate, onOpenHomeAdd, script]);

  /**
   * Ask welcome “Try an example — Customer Payments Hub”.
   * Same entity-first journey as Begin (create CPH → method → notes → extract).
   */
  const beginWithSamplePack = useCallback(() => {
    beginOnAddKnowledge();
  }, [beginOnAddKnowledge]);

  const loadSampleNotes = useCallback(() => {
    const notes = script?.sampleNotes || ADD_KNOWLEDGE_FIRST_APP.sampleNotes;
    try {
      window.dispatchEvent(
        new CustomEvent("dp-guide-load-sample", { detail: { text: notes } })
      );
    } catch {
      /* ignore */
    }
    patchState({ sampleNotesLoaded: true, wizardStep: "source" });
    // Persist resume payload immediately (don't wait for wizard-step echo).
    ensureGuideWizardPrefill({
      ...(state || {}),
      scriptId: state?.scriptId || ADD_KNOWLEDGE_FIRST_APP.id,
      chapterId: "supply",
      entityId: String(state?.entityId || "").trim(),
      entityName: String(state?.entityName || "").trim(),
      wizardStep: "source",
      sampleNotesLoaded: true,
    });
  }, [patchState, script, state]);

  const openInbox = useCallback(() => {
    onNavigate?.(V2_ROUTES.insightsInbox);
  }, [onNavigate]);

  const openReview = useCallback(() => {
    let jobId = String(state?.jobId || "").trim();
    if (!jobId) {
      const ready = (enrichmentJobs || []).find((j) => {
        const st = String(j?.status || "").toLowerCase();
        return st === "completed";
      });
      jobId = String(ready?.id || "").trim();
    }
    if (jobId) requestInboxOpenJob(jobId);
    onNavigate?.(V2_ROUTES.insightsInbox);
  }, [enrichmentJobs, onNavigate, state?.jobId]);

  const finishGuide = useCallback(() => {
    setGuideOptedOut(true);
    writeGuideOptedOut(true);
    persist(null);
    dismissInvite();
  }, [dismissInvite, persist]);

  // Empty map → auto-start first-run guide so the journey rail appears.
  // OnboardingVideoModal is not mounted in the shell anymore; do not wait
  // for video-dismissed (that gate left empty tenants with no rail forever).
  // Exit/Finish still opts out until the next empty-map epoch.
  useEffect(() => {
    if (mapIsEmpty !== true) return;
    if (guideOptedOut) return;
    if (state?.scriptId) return;
    startAddKnowledgeGuide();
  }, [guideOptedOut, mapIsEmpty, startAddKnowledgeGuide, state?.scriptId]);

  const openAsk = useCallback(() => {
    const name = state?.entityName || script?.sampleName || "this application";
    const id = state?.entityId || "";
    stashAgentPrefill({
      query: `Solution overview of ${name}`,
      entity: id
        ? { id, name, entity_type: "application" }
        : { name, entity_type: "application" },
    });
    try {
      window.dispatchEvent(
        new CustomEvent("dp-open-home-panel", { detail: { panel: "ask" } })
      );
    } catch {
      /* ignore */
    }
    if (typeof onOpenHomeAsk === "function") onOpenHomeAsk();
    else onNavigate?.(V2_ROUTES.home);
    finishGuide();
  }, [
    finishGuide,
    onNavigate,
    onOpenHomeAsk,
    script?.sampleName,
    state?.entityId,
    state?.entityName,
  ]);

  const openMap = useCallback(() => {
    onNavigate?.(V2_ROUTES.architectureGallery);
    finishGuide();
  }, [finishGuide, onNavigate]);

  /** Prefill + open Add knowledge for create-app / choose-how / supply. */
  const resumeAddKnowledge = useCallback(
    (expected) => {
      const s = script || ADD_KNOWLEDGE_FIRST_APP;
      const entityId = String(state?.entityId || "").trim();
      const entityName = String(
        state?.entityName || s.sampleName || ""
      ).trim();
      const startStep =
        expected?.wizard === "source" ||
        expected?.wizard === "pick" ||
        expected?.wizard === "anchor"
          ? expected.wizard
          : "anchor";
      if (entityId) {
        stashKnowledgePrefill({
          scopeType: "entity",
          id: entityId,
          name: entityName,
          suggestedType: "application",
          startStep,
          pasteText: s.sampleNotes,
        });
      } else {
        stashKnowledgePrefill({
          scopeType: "entity",
          intent: "create",
          suggestedName: entityName || s.sampleName,
          suggestedType: "application",
          startStep: "anchor",
          pasteText: s.sampleNotes,
        });
      }
      markKeepKnowledgePrefill();
      const onAdd =
        String(currentRoute || "") === V2_ROUTES.addKnowledge ||
        String(currentRoute || "") === "add-knowledge";
      if (onAdd) return;
      if (typeof onOpenHomeAdd === "function") onOpenHomeAdd();
      else onNavigate?.(V2_ROUTES.addKnowledge);
    },
    [
      currentRoute,
      onNavigate,
      onOpenHomeAdd,
      script,
      state?.entityId,
      state?.entityName,
    ]
  );

  /** Navigate the user back to the screen this chapter expects. */
  const resumeGuide = useCallback((chapterIdOverride) => {
    const chapterId = String(
      chapterIdOverride || chapter?.id || ""
    ).trim();
    const expected = expectedLocationForChapter(chapterId);
    if (!expected?.route) return;
    if (
      expected.route === V2_ROUTES.addKnowledge ||
      expected.route === "add-knowledge" ||
      expected.allowMapAdd
    ) {
      resumeAddKnowledge(expected);
      return;
    }
    if (expected.route === V2_ROUTES.home) {
      const panel =
        expected.panel || (expected.allowHomeAsk ? "ask" : "add");
      setHomePanel(panel);
      if (panel === "ask" || (expected.allowHomeAsk && panel !== "add")) {
        try {
          window.dispatchEvent(
            new CustomEvent("dp-open-home-panel", {
              detail: { panel: "ask" },
            })
          );
        } catch {
          /* ignore */
        }
        if (typeof onOpenHomeAsk === "function") onOpenHomeAsk();
        else onNavigate?.(V2_ROUTES.home);
        return;
      }
      onNavigate?.(V2_ROUTES.home);
      return;
    }
    if (expected.route === V2_ROUTES.insightsInbox) {
      openReview();
    } else {
      onNavigate?.(expected.route);
    }
  }, [
    chapter?.id,
    onNavigate,
    onOpenHomeAsk,
    openReview,
    resumeAddKnowledge,
  ]);

  const offPath = useMemo(
    () =>
      Boolean(
        state?.scriptId &&
          isGuideOffPath({
            chapterId: state?.chapterId,
            currentRoute,
            homePanel,
            mapAddOpen,
          })
      ),
    [currentRoute, homePanel, mapAddOpen, state?.chapterId, state?.scriptId]
  );

  const focusActive = Boolean(
    state?.scriptId && isGuideFocusChapter(state?.chapterId)
  );

  const nudgeFocus = useCallback(() => {
    setFocusNudge(true);
  }, []);

  const resumeGuideRef = useRef(resumeGuide);
  resumeGuideRef.current = resumeGuide;

  /** Soft focus: refuse disallowed nav in place (no navigate + snap = no flicker). */
  const guardNavigate = useCallback(
    (targetRoute) => {
      const chapterId = state?.chapterId;
      const target = String(targetRoute || "").trim();
      if (
        state?.scriptId &&
        isGuideFocusChapter(chapterId) &&
        !isGuideNavAllowed({ chapterId, targetRoute: target })
      ) {
        setFocusNudge(true);
        return;
      }
      // Already on this primary route — ignore so the shell does not remount/flicker.
      if (target && target === String(currentRoute || "").trim()) {
        return;
      }
      onNavigate?.(targetRoute);
    },
    [currentRoute, onNavigate, state?.chapterId, state?.scriptId]
  );

  // Soft focus auto-snap: leaving the step does not stick.
  useEffect(() => {
    if (!focusActive || !offPath) return undefined;
    const timer = window.setTimeout(() => {
      setFocusNudge(true);
      resumeGuideRef.current?.();
    }, 80);
    return () => window.clearTimeout(timer);
  }, [focusActive, offPath]);

  // Clear Soft-focus cue once back on path (or after a few seconds).
  useEffect(() => {
    if (!focusNudge) return undefined;
    if (!offPath) {
      setFocusNudge(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setFocusNudge(false), 4000);
    return () => window.clearTimeout(timer);
  }, [focusNudge, offPath]);

  const primaryAction = useCallback(() => {
    if (offPath) {
      resumeGuide();
      return;
    }
    const action = chapter?.primaryAction;
    if (action === "begin") beginOnAddKnowledge();
    else if (action === "load-sample") loadSampleNotes();
    else if (action === "open-inbox") openInbox();
    else if (action === "open-review") openReview();
    else if (action === "open-ask") openAsk();
    else if (action === "open-map") openMap();
    else if (action === "complete") finishGuide();
  }, [
    beginOnAddKnowledge,
    chapter?.primaryAction,
    finishGuide,
    loadSampleNotes,
    offPath,
    openAsk,
    openInbox,
    openMap,
    openReview,
    resumeGuide,
  ]);

  const secondaryAction = useCallback(() => {
    const action = chapter?.secondaryAction;
    if (action === "complete") finishGuide();
  }, [chapter?.secondaryAction, finishGuide]);

  // Product-screen events — catch-up safe (late events still advance).
  useEffect(() => {
    const onEvent = (ev) => {
      const detail = ev?.detail || {};
      const type = String(detail.type || "").trim();
      if (!type) return;

      if (type === "home-panel") {
        const panel = String(detail.panel || "hub").trim() || "hub";
        setHomePanel(panel);
        // Opening Add knowledge while still on intent → start create-app.
        if (panel === "add" && state?.scriptId && state?.chapterId === "intent") {
          beginOnAddKnowledge();
        }
        return;
      }

      if (type === "map-add-panel") {
        const open = Boolean(detail.open);
        setMapAddOpen(open);
        // Map Add knowledge opened while still on intent → advance to create-app.
        if (open && state?.scriptId && state?.chapterId === "intent") {
          const s = script || ADD_KNOWLEDGE_FIRST_APP;
          setState((prev) => {
            if (!prev?.scriptId) return prev;
            const next = applyChapterCompletion(s, prev, "intent");
            writeGuideState(next);
            return next;
          });
        }
        return;
      }

      if (!state?.scriptId) return;

      if (type === "wizard-step") {
        const step = String(detail.step || "").trim();
        const hasAnchor = Boolean(detail.hasAnchor);
        const hasNotes = Boolean(detail.hasNotes);
        setWizardUi({
          step,
          hasAnchor,
          hasNotes,
        });
        setState((prev) => {
          if (!prev?.scriptId) return prev;
          const s = getScript(prev.scriptId);
          if (!s) return prev;
          let next = alignChapterToWizardStep(s, prev, step);
          if (hasNotes && !next?.sampleNotesLoaded) {
            next = { ...next, sampleNotesLoaded: true };
          }
          if (next !== prev) writeGuideState(next);
          // Keep session prefill aligned so refresh restores this exact step.
          if (next?.entityId && (step === "source" || step === "pick" || step === "anchor")) {
            ensureGuideWizardPrefill({
              ...next,
              wizardStep: step,
              sampleNotesLoaded: Boolean(next.sampleNotesLoaded || hasNotes),
            });
          }
          return next;
        });
        return;
      }

      if (type === "anchor-ready") {
        const entityId = String(detail.entityId || "").trim();
        const entityName = String(detail.entityName || "").trim();
        patchState({
          entityId,
          entityName,
          wizardStep: "anchor",
        });
        // Tip/CTA sync: don't wait for a later wizard-step emit.
        setWizardUi((prev) => ({
          step: prev?.step || "anchor",
          hasAnchor: Boolean(entityId) || Boolean(prev?.hasAnchor),
          hasNotes: Boolean(prev?.hasNotes),
        }));
        // Keep resume prefill warm for refresh.
        if (entityId) {
          ensureGuideWizardPrefill({
            ...(state || {}),
            scriptId: state?.scriptId || ADD_KNOWLEDGE_FIRST_APP.id,
            chapterId: state?.chapterId || "create-app",
            entityId,
            entityName,
            wizardStep: "anchor",
          });
        }
        return;
      }
      if (type === "anchor-continued") {
        applyEventProgress("anchor-continued", {
          entityId: String(detail.entityId || state?.entityId || "").trim(),
          entityName: String(detail.entityName || state?.entityName || "").trim(),
        });
        return;
      }
      if (type === "method-picked") {
        applyEventProgress("method-picked");
        return;
      }
      if (type === "job-queued") {
        const jobId = String(detail.jobId || "").trim();
        // Leave the wizard arc — clear live step + open-add so refresh
        // resumes Inbox, not Add knowledge.
        setWizardUi(null);
        clearOpenAddPanel();
        clearKnowledgePrefill();
        applyEventProgress("job-queued", {
          ...(jobId ? { jobId } : {}),
          // CPH sample ETA countdown — persist so refresh keeps the clock.
          processingStartedAt: Date.now(),
        });
        refreshEnrichmentJobs?.();
        return;
      }
      if (type === "review-opened") {
        return;
      }
      if (type === "review-complete") {
        applyEventProgress("review-complete");
      }
    };
    window.addEventListener(GUIDE_EVENT, onEvent);
    return () => window.removeEventListener(GUIDE_EVENT, onEvent);
  }, [
    applyEventProgress,
    beginOnAddKnowledge,
    patchState,
    refreshEnrichmentJobs,
    script,
    state?.entityId,
    state?.entityName,
    state?.scriptId,
    state?.chapterId,
  ]);

  // Gate: job ready for review — catch-up even if strip lagged on supply.
  useEffect(() => {
    if (!state?.scriptId) return;
    const jobId = String(state.jobId || "").trim();
    if (!jobId) return;
    const job = (enrichmentJobs || []).find(
      (j) => String(j?.id || "").trim() === jobId
    );
    if (!job) return;
    const st = String(job.status || "").toLowerCase();
    if (st !== "completed") return;
    const ch = String(state.chapterId || "");
    if (ch !== "processing" && ch !== "supply") return;
    applyEventProgress("job-ready");
    if (ch === "processing") requestInboxOpenJob(jobId);
  }, [
    applyEventProgress,
    enrichmentJobs,
    state?.chapterId,
    state?.jobId,
    state?.scriptId,
  ]);

  // Backfill processingStartedAt for CPH Building (refresh mid-flight, or
  // older guide state without the timestamp).
  useEffect(() => {
    if (!state?.scriptId) return;
    if (String(state.chapterId || "") !== "processing") return;
    if (Number(state.processingStartedAt) > 0) return;
    const jobId = String(state.jobId || "").trim();
    const job = jobId
      ? (enrichmentJobs || []).find(
          (j) => String(j?.id || "").trim() === jobId
        )
      : null;
    const fromJob = Date.parse(String(job?.created_at || ""));
    const startedAt =
      Number.isFinite(fromJob) && fromJob > 0 ? fromJob : Date.now();
    patchState({ processingStartedAt: startedAt });
  }, [
    enrichmentJobs,
    patchState,
    state?.chapterId,
    state?.jobId,
    state?.processingStartedAt,
    state?.scriptId,
  ]);

  const active = Boolean(state?.scriptId && script && chapter);

  const resumeLabel = useMemo(() => {
    if (!offPath && !focusNudge) return null;
    const expected = expectedLocationForChapter(chapter?.id);
    if (expected?.label) return `Back to ${expected.label}`;
    return "Continue guide";
  }, [chapter?.id, focusNudge, offPath]);

  const value = useMemo(
    () => ({
      active,
      // Retired: the newcomer invite strip was removed — the Ask welcome
      // screen (example questions / sample pack) is the entry point now.
      // Kept in the context shape so existing consumers don't break.
      showInvite: false,
      showOnboardingVideo:
        !active && !onboardingVideoDismissed && mapIsEmpty === true,
      state,
      script,
      chapter,
      chapterIndex,
      chapterCount: script?.chapters?.length || 0,
      offPath,
      focusActive,
      focusNudge,
      resumeLabel,
      resumeGuide,
      guardNavigate,
      nudgeFocus,
      startAddKnowledgeGuide,
      skipGuide,
      dismissInvite,
      dismissOnboardingVideo,
      goToChapter,
      primaryAction,
      secondaryAction,
      beginOnAddKnowledge,
      beginWithSamplePack,
      currentRoute,
      homePanel,
      wizardUi,
    }),
    [
      active,
      beginOnAddKnowledge,
      beginWithSamplePack,
      chapter,
      chapterIndex,
      currentRoute,
      dismissInvite,
      dismissOnboardingVideo,
      focusActive,
      focusNudge,
      goToChapter,
      guardNavigate,
      homePanel,
      inviteDismissed,
      mapIsEmpty,
      nudgeFocus,
      offPath,
      onboardingVideoDismissed,
      primaryAction,
      resumeGuide,
      resumeLabel,
      secondaryAction,
      script,
      skipGuide,
      startAddKnowledgeGuide,
      state,
      wizardUi,
    ]
  );

  return (
    <GuideContext.Provider value={value}>{children}</GuideContext.Provider>
  );
}

export function useProductGuide() {
  return useContext(GuideContext);
}

export function useOptionalProductGuide() {
  return useContext(GuideContext);
}
